import { forwardRef, InputHTMLAttributes, isValidElement, ReactNode, useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Eye, EyeOff } from 'lucide-react'

export interface InputAffixOption {
    label: string
    value: string
}

export interface InputAffixConfig {
    icon?: ReactNode
    content?: string
    showArrow?: boolean
    divider?: 'none' | 'left' | 'right'
    options?: InputAffixOption[]
    value?: string
    defaultValue?: string
    onChange?: (value: string) => void
    onClick?: () => void
}

export interface InputGroupProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'prefix'> {
    variant?: 'primary' | 'secondary'
    inputType?: 'text' | 'number' | 'password'
    prefix?: ReactNode | InputAffixConfig
    suffix?: ReactNode | InputAffixConfig
    gapSpace?: boolean
}

const isAffixConfig = (v: unknown): v is InputAffixConfig => typeof v === 'object' && v !== null && !isValidElement(v)

interface AffixProps {
    config: InputAffixConfig
    side: 'left' | 'right'
    bgClass: string
    gapSpace: boolean
}

const Affix = ({ config, side, bgClass, gapSpace }: AffixProps) => {
    const { icon, content = '1+', showArrow = true, divider = 'none', options, onChange, onClick } = config
    const [open, setOpen] = useState(false)
    const [internalValue, setInternalValue] = useState<string | undefined>(config.defaultValue)
    const containerRef = useRef<HTMLDivElement>(null)

    const value = config.value !== undefined ? config.value : internalValue
    const hasOptions = options && options.length > 0
    const isActive = open || value !== undefined

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const handleClick = () => {
        if (hasOptions) setOpen((v) => !v)
        else onClick?.()
    }

    const handleSelect = (val: string) => {
        if (config.value === undefined) setInternalValue(val)
        onChange?.(val)
        setOpen(false)
    }

    const selectedLabel = options?.find((o) => o.value === value)?.label
    const displayContent = selectedLabel ?? content

    // Determine divider: if gapSpace is on, parent handles border; otherwise use config divider
    const showDivider = !gapSpace && divider !== 'none'

    return (
        <div ref={containerRef} className="relative flex h-full items-center">
            <button
                type="button"
                onClick={handleClick}
                className={`flex h-full shrink-0 cursor-pointer items-center gap-1.5 border-none px-3 py-2 transition-colors duration-100 ${bgClass} ${
                    isActive ? 'text-foreground' : 'text-foreground-muted'
                } ${side === 'left' ? 'rounded-l-xl' : 'rounded-r-xl'}`}
            >
                {/* Icon */}
                <span className="flex h-3.5 w-3.5 shrink-0 items-center">
                    {icon ?? (
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 2" />
                        </svg>
                    )}
                </span>

                {/* Text + chevron */}
                <span className="flex shrink-0 items-center gap-1">
                    <span className="whitespace-nowrap text-sm font-normal leading-5">{displayContent}</span>
                    {showArrow && (
                        <span className={`flex items-center transition-transform duration-150 ${open ? 'rotate-180' : 'rotate-0'}`}>
                            <ChevronDown size={16} strokeWidth={1.5} />
                        </span>
                    )}
                </span>
            </button>

            {/* Divider from config (when gapSpace is false) */}
            {showDivider && <div className={`pointer-events-none absolute inset-y-0 w-px bg-border ${divider === 'right' ? 'right-0' : 'left-0'}`} />}

            {/* Dropdown */}
            {open && hasOptions && (
                <div
                    className={`absolute top-[calc(100%+8px)] z-[100] flex min-w-[160px] flex-col rounded-3xl bg-surface p-1 shadow-overlay backdrop-blur-[20px] ${
                        side === 'right' ? 'right-0' : 'left-0'
                    }`}
                >
                    {options!.map((opt) => {
                        const isSelected = opt.value === value
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => handleSelect(opt.value)}
                                className="flex min-h-9 w-full cursor-pointer items-center justify-between gap-3 rounded-[20px] border-none bg-transparent px-3 py-1.5 text-left text-sm font-medium leading-5 text-foreground hover:bg-default"
                            >
                                <span>{opt.label}</span>
                                {isSelected && (
                                    <span className="flex shrink-0 items-center">
                                        <Check size={16} strokeWidth={1.5} />
                                    </span>
                                )}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export const InputGroup = forwardRef<HTMLInputElement, InputGroupProps>(
    ({ variant = 'primary', inputType = 'text', prefix, suffix, gapSpace = false, style, ...props }, ref) => {
        const [focused, setFocused] = useState(false)
        const [showPassword, setShowPassword] = useState(false)

        const isSecondary = variant === 'secondary'
        const isPassword = inputType === 'password'
        const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : inputType

        const bgClass = isSecondary ? 'bg-default' : 'bg-surface'
        const chromeClass = `${bgClass} ${focused ? 'ring-2 ring-accent' : isSecondary ? '' : 'shadow-field'}`

        const hasPrefix = prefix !== undefined && prefix !== null
        const hasSuffix = suffix !== undefined && suffix !== null

        const renderAffix = (affix: ReactNode | InputAffixConfig, side: 'left' | 'right') => {
            if (isAffixConfig(affix)) {
                return <Affix config={affix} side={side} bgClass={bgClass} gapSpace={gapSpace} />
            }
            return (
                <div
                    className={`flex h-full shrink-0 items-center gap-1.5 px-3 py-2 text-sm font-normal leading-5 text-foreground-muted ${bgClass} ${
                        side === 'left' ? 'rounded-l-xl' : 'rounded-r-xl'
                    } ${gapSpace && side === 'left' ? 'border-r border-border' : ''} ${gapSpace && side === 'right' ? 'border-l border-border' : ''}`}
                >
                    {affix}
                </div>
            )
        }

        return (
            <div
                className={`relative flex h-9 items-center overflow-visible rounded-xl transition-shadow duration-150 ${
                    gapSpace ? 'gap-3' : 'gap-0.5'
                } ${chromeClass} ${props.disabled ? 'opacity-50' : ''}`}
                style={style}
            >
                {/* Clip the background but allow dropdown overflow */}
                <div className={`pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-xl ${chromeClass}`} />

                {hasPrefix && (
                    <div className={`relative z-[1] flex h-full items-center ${gapSpace && isAffixConfig(prefix) ? 'border-r border-border' : ''}`}>
                        {renderAffix(prefix, 'left')}
                    </div>
                )}

                <input
                    ref={ref}
                    type={resolvedType}
                    className={`relative z-[1] h-full min-w-0 flex-1 border-none bg-transparent py-2 text-sm font-normal leading-5 text-foreground outline-none ${
                        hasPrefix ? 'pl-0' : 'pl-3'
                    } ${hasSuffix || isPassword ? 'pr-0' : 'pr-3'} ${props.disabled ? 'cursor-not-allowed' : 'cursor-text'}`}
                    {...props}
                    onFocus={(e) => {
                        setFocused(true)
                        props.onFocus?.(e)
                    }}
                    onBlur={(e) => {
                        setFocused(false)
                        props.onBlur?.(e)
                    }}
                />

                {isPassword && (
                    <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => setShowPassword((v) => !v)}
                        className={`relative z-[1] flex h-full shrink-0 cursor-pointer items-center border-none bg-transparent px-3 py-2 text-foreground-muted ${
                            !hasSuffix ? 'rounded-r-xl' : ''
                        }`}
                    >
                        {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                )}

                {hasSuffix && (
                    <div className={`relative z-[1] flex h-full items-center ${gapSpace && isAffixConfig(suffix) ? 'border-l border-border' : ''}`}>
                        {renderAffix(suffix, 'right')}
                    </div>
                )}
            </div>
        )
    }
)
InputGroup.displayName = 'InputGroup'
